// useGlobalShortcut — document-level single-key shortcut (CTSK-493 §5.6).
//
// Registers a keydown listener on `document` that fires `handler` when
// `key` is pressed with no modifier and the user is not already typing
// somewhere (input / textarea / select / contenteditable). Design doc
// §3.4: `/` focuses the search input "if no text input is already
// focused". Only registers while `enabled` (the feature flag — §7.2 says
// no `/` shortcut when the flag is off); unregisters on unmount.

import { useEffect } from 'react';

export interface GlobalShortcutOptions {
  /** Register the listener at all. Default true. */
  enabled?: boolean;
  /** Skip when the active element is a text-entry control. Default true. */
  ignoreTextInputs?: boolean;
}

const TEXT_INPUT_TYPES = new Set([
  'text', 'search', 'email', 'url', 'tel', 'password', 'number', 'date',
  'datetime-local', 'month', 'time', 'week',
]);

/** True when `el` accepts typed text — a `/` there is content, not a command. */
export function isTextEntryElement(el: Element | null): boolean {
  if (!el) return false;
  if (el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement) return true;
  if (el instanceof HTMLInputElement) {
    const type = (el.getAttribute('type') ?? 'text').toLowerCase();
    return TEXT_INPUT_TYPES.has(type);
  }
  if (el instanceof HTMLElement) {
    // `isContentEditable` is the live answer in browsers; the attribute
    // walk is the fallback where it isn't implemented (jsdom).
    if (el.isContentEditable) return true;
    const ce = el.closest('[contenteditable]')?.getAttribute('contenteditable');
    if (ce !== undefined && ce !== null && ce.toLowerCase() !== 'false') return true;
  }
  return false;
}

export function useGlobalShortcut(
  key: string,
  handler: () => void,
  { enabled = true, ignoreTextInputs = true }: GlobalShortcutOptions = {},
): void {
  useEffect(() => {
    if (!enabled) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.defaultPrevented) return;
      if (e.key !== key) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.isComposing) return;
      if (ignoreTextInputs && isTextEntryElement(document.activeElement)) return;
      e.preventDefault();
      handler();
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [key, handler, enabled, ignoreTextInputs]);
}
